import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, RefreshCw, Eye, Download, Loader2, CheckCircle2, AlertTriangle, XCircle, FileSearch, ChevronDown, ChevronRight, Package, Inbox, Wallet, FileSpreadsheet, FolderOpen, FileArchive, Bot, PenLine, History, ClipboardCheck, Users } from 'lucide-react';
import {
    listExtracoes, listLotes, listExtracoesDoLote, listExtracoesGestaoSemLote,
    listExtracoesPorOrigem, contarPorOrigem, carregarEstruturados,
    getExtracao, reprocessarExtracao, getPdfUrl, ORIGENS_EXTRACAO, type FiltrosHistorico,
} from '../../../src/services/extracaoApi';
import type { ExtracaoFaturaRow, ExtracaoLoteRow, OrigemExtracao } from '../../../types';
import { useToast } from '../../../src/contexts/ToastContext';
import { useExtracaoFaturas } from '../ExtracaoFaturasContext';
import RevisaoFaturaModal from './RevisaoFaturaModal';
import { exportExtracoesToExcel } from './excelExport';
import PlanilhaFaturasModal from './planilha/PlanilhaFaturasModal';
import type { ExcelFaturaItem } from './planilha/faturaGridModel';
import { escalaresDeCorrecoes } from './planilha/faturaGridModel';
import type { CorrecoesPlanilha } from '../../../types';
import { baixarPdfsZip } from './zipFaturas';
import { supabase } from '../../../src/lib/supabase';
import { getStatusRateioUc, STATUS_RATEIO_UC_META, type StatusRateioUc } from '../../../src/lib/statusRateioUc';

/** Converte linhas do banco (com dados_estruturados) no shape que a planilha/Excel consomem.
 *  `extracaoId` + `correcoes` são o que permite salvar (e reaplicar) a correção manual. */
const toExcelItems = (rows: ExtracaoFaturaRow[]): ExcelFaturaItem[] =>
    rows.filter(r => r.dados_estruturados)
        .map(r => ({
            arquivo: r.arquivo_nome || r.uc_codigo || 'fatura', estruturado: r.dados_estruturados!,
            reconciliacao: r.reconciliacao, pdfPath: r.pdf_storage_path,
            extracaoId: r.id, correcoes: r.correcoes_planilha,
        }));

const SEM_LOTE = '__sem_lote__';

type Aba = 'todas' | OrigemExtracao;

// Cada origem é um ponto de entrada diferente do MESMO robô determinístico. Antes
// a tela só sabia agrupar por lote — e como robô nunca manda lote_id, todo o
// tráfego dos robôs caía no balde "Extrações antigas (sem lote)".
const ORIGEM_INFO: Record<OrigemExtracao, { rotulo: string; descricao: string; icone: React.ElementType; cor: string }> = {
    'gestao': { rotulo: 'Gestão', descricao: 'Upload em lote na tela de extração', icone: FolderOpen, cor: 'slate' },
    'robo-manual': { rotulo: 'Robô Manual', descricao: 'n8n — importação de fatura em PDF no Financeiro', icone: Bot, cor: 'sky' },
    'robo-assinatura': { rotulo: 'Robô Assinatura', descricao: 'n8n — fatura definitiva / boleto emitido', icone: PenLine, cor: 'violet' },
    'robo-historico': { rotulo: 'Robô Histórico', descricao: 'Busca automática da última fatura de UCs sem histórico', icone: History, cor: 'teal' },
    'analise-cadastral': { rotulo: 'Validação de Dados', descricao: 'Fatura atualizada na Análise Técnica do wizard', icone: ClipboardCheck, cor: 'indigo' },
    'crm': { rotulo: 'CRM', descricao: 'Enviar fatura / Nova negociação automática', icone: Users, cor: 'rose' },
};

const CORES_CHIP: Record<string, string> = {
    slate: 'text-slate-700 bg-slate-100 dark:bg-slate-700/40 dark:text-slate-300',
    sky: 'text-sky-700 bg-sky-100 dark:bg-sky-900/30 dark:text-sky-300',
    violet: 'text-violet-700 bg-violet-100 dark:bg-violet-900/30 dark:text-violet-300',
    teal: 'text-teal-700 bg-teal-100 dark:bg-teal-900/30 dark:text-teal-300',
    indigo: 'text-indigo-700 bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-300',
    rose: 'text-rose-700 bg-rose-100 dark:bg-rose-900/30 dark:text-rose-300',
};

// ── Custos da IA (Google Document AI — único serviço pago do pipeline) ──
// Enterprise Document OCR: US$ 1,50 por 1.000 páginas. A fatura DANF3E tem 2 páginas.
// O OCR é pago UMA vez por upload; reprocessar/exportar reusa o OCR salvo e custa R$ 0.
const USD_POR_PAGINA = 1.5 / 1000;
const PAGINAS_POR_FATURA = 2;     // estimativa (extrações novas gravam o nº exato em meta.paginas)
const CAMBIO_BRL = 5.4;           // aproximação p/ exibição — ajustar se o câmbio mudar muito

const fmtUsd = (v: number) => `US$ ${v.toFixed(2)}`;
const fmtBrl = (v: number) => `R$ ${(v).toFixed(2).replace('.', ',')}`;

const statusChip = (st: string) => {
    if (st === 'ok') return <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 px-2 py-0.5 rounded"><CheckCircle2 className="w-3 h-3" /> Confere</span>;
    if (st === 'revisar') return <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-amber-700 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300 px-2 py-0.5 rounded"><AlertTriangle className="w-3 h-3" /> Revisar</span>;
    return <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-red-700 bg-red-100 dark:bg-red-900/30 dark:text-red-300 px-2 py-0.5 rounded"><XCircle className="w-3 h-3" /> Falhou</span>;
};

const origemChip = (origem: OrigemExtracao) => {
    const info = ORIGEM_INFO[origem];
    if (!info) return <span className="text-[11px] text-gray-400">{origem}</span>;
    const Icone = info.icone;
    return (
        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded whitespace-nowrap ${CORES_CHIP[info.cor]}`} title={info.descricao}>
            <Icone className="w-3 h-3" /> {info.rotulo}
        </span>
    );
};

const fmtData = (iso: string) => { try { return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }); } catch { return iso; } };

const HistoricoExtracoesView: React.FC = () => {
    const { addToast } = useToast();
    const navigate = useNavigate();
    const { files: filaExtracao, isProcessing, abrirLote } = useExtracaoFaturas();

    const [aba, setAba] = useState<Aba>('todas');
    const [contagens, setContagens] = useState<Record<OrigemExtracao, number> | null>(null);

    // Filtros / busca. Quando algum está ativo a visão vira tabela plana, mesmo na
    // aba Gestão — achar UMA fatura é incompatível com navegar por lote.
    const [filtros, setFiltros] = useState<FiltrosHistorico>({});
    const [busca, setBusca] = useState('');
    const filtrando = !!(filtros.busca || filtros.status || filtros.grupo);
    // Só a Gestão usa lote; as outras origens gravam linhas soltas.
    const modoLotes = aba === 'gestao' && !filtrando;

    // Tabela plana (busca ou aba de origem)
    const [rows, setRows] = useState<ExtracaoFaturaRow[]>([]);
    const [pagina, setPagina] = useState(0);
    const [temMais, setTemMais] = useState(false);
    const [loadingLista, setLoadingLista] = useState(false);
    const [erroLista, setErroLista] = useState<string | null>(null);

    // Modo agrupado por lote (aba Gestão)
    const [lotes, setLotes] = useState<ExtracaoLoteRow[]>([]);
    const [semLote, setSemLote] = useState<ExtracaoFaturaRow[]>([]);
    const [loadingGrupos, setLoadingGrupos] = useState(false);
    const [erroGrupos, setErroGrupos] = useState<string | null>(null);
    const [abertos, setAbertos] = useState<Set<string>>(new Set());
    const [faturasDoLote, setFaturasDoLote] = useState<Record<string, ExtracaoFaturaRow[]>>({});
    const [carregandoLote, setCarregandoLote] = useState<string | null>(null);
    const [exportandoLoteId, setExportandoLoteId] = useState<string | null>(null);

    const [revisao, setRevisao] = useState<{ row: ExtracaoFaturaRow; pdfUrl: string | null } | null>(null);
    const [acaoId, setAcaoId] = useState<string | null>(null);
    const [custosAbertos, setCustosAbertos] = useState(false);
    const [planilha, setPlanilha] = useState<{ faturas: ExcelFaturaItem[]; titulo: string } | null>(null);
    const [planilhaLoteId, setPlanilhaLoteId] = useState<string | null>(null);
    const [abrindoLoteId, setAbrindoLoteId] = useState<string | null>(null);
    const [preparandoExport, setPreparandoExport] = useState(false);
    const [zipLote, setZipLote] = useState<{ id: string; feitos: number; total: number } | null>(null);
    const zipCancelRef = useRef(false);

    // Status de rateio da UC (derivado, ver src/lib/statusRateioUc.ts) — buscado por
    // uc_codigo (texto da fatura, sem FK) contra unidades_consumidoras.numero_uc/outro_numero_uc.
    const [statusPorUc, setStatusPorUc] = useState<Record<string, StatusRateioUc>>({});
    const codigosVisiveis = useMemo(() => {
        const codigos = new Set<string>();
        rows.forEach(r => r.uc_codigo && codigos.add(r.uc_codigo));
        semLote.forEach(r => r.uc_codigo && codigos.add(r.uc_codigo));
        Object.values(faturasDoLote).forEach(lista => lista.forEach(r => r.uc_codigo && codigos.add(r.uc_codigo)));
        return Array.from(codigos);
    }, [rows, semLote, faturasDoLote]);

    useEffect(() => {
        const pendentes = codigosVisiveis.filter(c => !(c in statusPorUc));
        if (pendentes.length === 0) return;
        (async () => {
            const campos = 'numero_uc, outro_numero_uc, situacao, cadastro_aprovado, rateio_motivo_travamento, dados_tecnicos_equatorial, rateio_unidade_geradora_id, unidade_geradora';
            const [porNumero, porOutro] = await Promise.all([
                supabase.from('unidades_consumidoras').select(campos).in('numero_uc', pendentes),
                supabase.from('unidades_consumidoras').select(campos).in('outro_numero_uc', pendentes),
            ]);
            const novos: Record<string, StatusRateioUc> = {};
            [...(porNumero.data || []), ...(porOutro.data || [])].forEach((uc: any) => {
                const status = getStatusRateioUc(uc);
                if (uc.numero_uc) novos[uc.numero_uc] = status;
                if (uc.outro_numero_uc) novos[uc.outro_numero_uc] = status;
            });
            // Código sem correspondência cai no fallback "UC inativa" — evita refazer a
            // busca em loop pro mesmo uc_codigo sem match.
            pendentes.forEach(c => { if (!(c in novos)) novos[c] = 'inativa'; });
            setStatusPorUc(prev => ({ ...prev, ...novos }));
        })();
    }, [codigosVisiveis]);

    const carregarContagens = useCallback(async () => {
        try { setContagens(await contarPorOrigem()); } catch { /* badge é enfeite, não trava a tela */ }
    }, []);

    const carregarLista = useCallback(async () => {
        setLoadingLista(true); setErroLista(null);
        try {
            if (filtrando) {
                const origem = aba === 'todas' ? undefined : aba;
                setRows(await listExtracoes({ ...filtros, origem }));
                setTemMais(false);
            } else if (aba === 'todas') {
                setRows(await listExtracoes({}));
                setTemMais(false);
            } else {
                const { rows: r, temMais: m } = await listExtracoesPorOrigem(aba, 0);
                setRows(r); setTemMais(m);
            }
            setPagina(0);
        } catch (e: any) {
            setErroLista(e?.message || 'Falha ao carregar o histórico.');
        } finally { setLoadingLista(false); }
    }, [aba, filtros, filtrando]);

    const carregarMais = async () => {
        if (aba === 'todas' || filtrando) return;
        setLoadingLista(true);
        try {
            const proxima = pagina + 1;
            const { rows: r, temMais: m } = await listExtracoesPorOrigem(aba, proxima);
            setRows(prev => [...prev, ...r]);
            setPagina(proxima); setTemMais(m);
        } catch (e: any) {
            addToast(e?.message || 'Falha ao carregar mais extrações.', 'error');
        } finally { setLoadingLista(false); }
    };

    const carregarGrupos = useCallback(async () => {
        setLoadingGrupos(true); setErroGrupos(null);
        try {
            const [l, s] = await Promise.all([listLotes(), listExtracoesGestaoSemLote()]);
            setLotes(l); setSemLote(s);
        } catch (e: any) {
            setErroGrupos(e?.message || 'Falha ao carregar o histórico.');
        } finally { setLoadingGrupos(false); }
    }, []);

    useEffect(() => { carregarContagens(); }, [carregarContagens]);
    useEffect(() => { if (modoLotes) carregarGrupos(); else carregarLista(); }, [modoLotes, carregarGrupos, carregarLista]);

    const atualizar = () => {
        carregarContagens();
        if (modoLotes) { carregarGrupos(); setFaturasDoLote({}); } else carregarLista();
    };

    const aplicarBusca = (e: React.FormEvent) => { e.preventDefault(); setFiltros(f => ({ ...f, busca })); };

    const trocarAba = (nova: Aba) => { setAba(nova); setRows([]); setPagina(0); };

    const toggleLote = async (id: string) => {
        setAbertos(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
        if (id !== SEM_LOTE && !faturasDoLote[id]) {
            setCarregandoLote(id);
            try {
                const dados = await listExtracoesDoLote(id);
                setFaturasDoLote(prev => ({ ...prev, [id]: dados }));
            } catch (e: any) {
                addToast(e?.message || 'Falha ao carregar as faturas do lote.', 'error');
            } finally { setCarregandoLote(null); }
        }
    };

    const abrirRevisao = async (id: string, pdfPath: string | null) => {
        setAcaoId(id);
        try {
            const full = await getExtracao(id);
            if (full) {
                const pdfUrl = await getPdfUrl(pdfPath);
                setRevisao({ row: full, pdfUrl });
            }
        } catch (e: any) {
            addToast(e?.message || 'Falha ao abrir a fatura.', 'error');
        } finally { setAcaoId(null); }
    };

    const reprocessar = async (id: string) => {
        setAcaoId(id);
        try {
            await reprocessarExtracao(id);
            addToast('Fatura reprocessada.', 'success');
            atualizar();
        } catch (e: any) {
            addToast(e?.message || 'Falha ao reprocessar.', 'error');
        } finally { setAcaoId(null); }
    };

    /** A planilha acabou de gravar correção manual: reflete aqui (tabela, caches e a
     *  própria planilha aberta) para a lista não ficar mostrando o valor antigo. */
    const aplicarCorrecoesLocais = useCallback((porExtracao: Record<string, CorrecoesPlanilha>) => {
        const atualiza = (r: ExtracaoFaturaRow): ExtracaoFaturaRow => {
            const corr = porExtracao[r.id];
            return corr ? { ...r, correcoes_planilha: corr, ...escalaresDeCorrecoes(corr) } : r;
        };
        setRows(prev => prev.map(atualiza));
        setSemLote(prev => prev.map(atualiza));
        setFaturasDoLote(prev => Object.fromEntries(Object.entries(prev).map(([k, v]) => [k, v.map(atualiza)])));
        setPlanilha(prev => prev && {
            ...prev,
            faturas: prev.faturas.map(f => (f.extracaoId && porExtracao[f.extracaoId] ? { ...f, correcoes: porExtracao[f.extracaoId] } : f)),
        });
    }, []);

    const exportarUm = async (id: string) => {
        setAcaoId(id);
        try {
            const full = await getExtracao(id);
            if (full?.dados_estruturados) {
                await exportExtracoesToExcel([{ arquivo: full.arquivo_nome || full.uc_codigo || 'fatura', estruturado: full.dados_estruturados, reconciliacao: full.reconciliacao }]);
            } else {
                addToast('Essa extração não tem dados estruturados salvos para exportar.', 'warning');
            }
        } catch (e: any) {
            addToast(e?.message || 'Falha ao exportar essa fatura.', 'error');
        } finally { setAcaoId(null); }
    };

    /** A lista não traz dados_estruturados (JSONB pesado) — busca sob demanda na hora
     *  de exportar/abrir planilha. Linhas que já vieram completas passam direto. */
    const comEstruturados = async (dados: ExtracaoFaturaRow[]): Promise<ExtracaoFaturaRow[]> => {
        const faltando = dados.filter(r => !r.dados_estruturados).map(r => r.id);
        if (faltando.length === 0) return dados;
        const completas = await carregarEstruturados(faltando);
        const porId = new Map(completas.map(r => [r.id, r]));
        return dados.map(r => porId.get(r.id) || r);
    };

    const dadosDoGrupo = async (id: string): Promise<ExtracaoFaturaRow[]> => {
        if (id === SEM_LOTE) return comEstruturados(semLote);
        const cache = faturasDoLote[id];
        if (cache) return cache;
        const dados = await listExtracoesDoLote(id);
        setFaturasDoLote(prev => ({ ...prev, [id]: dados }));
        return dados;
    };

    const exportarLote = async (id: string) => {
        setExportandoLoteId(id);
        try {
            const dados = await dadosDoGrupo(id);
            const comDados = dados.filter(r => r.dados_estruturados);
            if (comDados.length === 0) {
                addToast('Nenhuma fatura desse lote tem dados estruturados para exportar.', 'warning');
                return;
            }
            await exportExtracoesToExcel(comDados.map(r => ({ arquivo: r.arquivo_nome || r.uc_codigo || 'fatura', estruturado: r.dados_estruturados!, reconciliacao: r.reconciliacao })));
        } catch (e: any) {
            addToast(e?.message || 'Falha ao exportar o lote.', 'error');
        } finally { setExportandoLoteId(null); }
    };

    const abrirPlanilhaLote = async (id: string, titulo: string) => {
        setPlanilhaLoteId(id);
        try {
            const items = toExcelItems(await dadosDoGrupo(id));
            if (items.length === 0) { addToast('Nenhuma fatura desse lote tem dados estruturados para exibir.', 'warning'); return; }
            setPlanilha({ faturas: items, titulo });
        } catch (e: any) {
            addToast(e?.message || 'Falha ao abrir a planilha do lote.', 'error');
        } finally { setPlanilhaLoteId(null); }
    };

    /** Baixa TODOS os PDFs originais do lote num único .zip (clicar de novo cancela). */
    const baixarZipLote = async (id: string, titulo: string) => {
        if (zipLote) return;
        zipCancelRef.current = false;
        setZipLote({ id, feitos: 0, total: 0 });
        try {
            const dados = await dadosDoGrupo(id);
            const itens = dados.map(r => ({ nome: r.arquivo_nome || r.uc_codigo || 'fatura', path: r.pdf_storage_path }));
            const { adicionados, faltando, cancelado } = await baixarPdfsZip(
                itens,
                `${titulo.replace(/[^\w.-]+/g, '_')}_PDFs`,
                (feitos, total) => setZipLote({ id, feitos, total }),
                () => zipCancelRef.current,
            );
            if (cancelado) addToast('Download do ZIP cancelado.', 'info');
            else if (adicionados === 0) addToast('Nenhuma fatura desse lote tem PDF salvo no Storage.', 'warning');
            else if (faltando.length > 0) addToast(`ZIP gerado com ${adicionados} PDF(s); ${faltando.length} não foram encontrados no Storage.`, 'warning');
        } catch (e: any) {
            addToast(e?.message || 'Falha ao gerar o ZIP do lote.', 'error');
        } finally { setZipLote(null); }
    };

    /** Torna o lote o ativo da tela de extração e navega pra lá (revisar/reprocessar/completar). */
    const abrirNaExtracao = async (id: string) => {
        if (isProcessing) {
            addToast('Há uma extração em andamento — pare ou aguarde antes de abrir outro lote.', 'warning');
            return;
        }
        const naoSalvos = filaExtracao.filter(f => f.status === 'pending' && f.file).length;
        if (naoSalvos > 0 && !window.confirm(`A tela de extração tem ${naoSalvos} arquivo(s) aguardando processamento que serão descartados. Abrir este lote mesmo assim?`)) return;
        setAbrindoLoteId(id);
        try {
            await abrirLote(id);
            navigate('/gestao/extracao');
        } catch (e: any) {
            addToast(e?.message || 'Falha ao abrir o lote na tela de extração.', 'error');
        } finally { setAbrindoLoteId(null); }
    };

    const abrirPlanilhaLista = async () => {
        setPreparandoExport(true);
        try {
            const items = toExcelItems(await comEstruturados(rows));
            if (items.length === 0) { addToast('Nenhum resultado tem dados estruturados para exibir.', 'warning'); return; }
            setPlanilha({ faturas: items, titulo: aba === 'todas' ? 'Extracoes' : `Extracoes_${ORIGEM_INFO[aba].rotulo}` });
        } catch (e: any) {
            addToast(e?.message || 'Falha ao montar a planilha.', 'error');
        } finally { setPreparandoExport(false); }
    };

    const renderTabelaFaturas = (dados: ExtracaoFaturaRow[], mostrarOrigem: boolean) => (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-left text-gray-400 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50">
                        <th className="py-2.5 px-4 font-medium">Status</th>
                        {mostrarOrigem && <th className="py-2.5 px-4 font-medium">Origem</th>}
                        <th className="py-2.5 px-4 font-medium">UC</th>
                        <th className="py-2.5 px-4 font-medium">Status Rateio</th>
                        <th className="py-2.5 px-4 font-medium">Cliente</th>
                        <th className="py-2.5 px-4 font-medium">Mês</th>
                        <th className="py-2.5 px-4 font-medium text-center">Grupo</th>
                        <th className="py-2.5 px-4 font-medium text-right">Total</th>
                        <th className="py-2.5 px-4 font-medium text-center">Score</th>
                        <th className="py-2.5 px-4 font-medium">Quem / Quando</th>
                        <th className="py-2.5 px-4 font-medium text-right">Ações</th>
                    </tr>
                </thead>
                <tbody>
                    {dados.map(r => (
                        <tr key={r.id} className="border-b border-gray-50 dark:border-gray-800/50 hover:bg-gray-50/50 dark:hover:bg-gray-800/30">
                            <td className="py-2.5 px-4">{statusChip(r.status)}</td>
                            {mostrarOrigem && <td className="py-2.5 px-4">{origemChip(r.origem)}</td>}
                            <td className="py-2.5 px-4 font-mono text-[12px] text-gray-700 dark:text-gray-200">{r.uc_codigo || '—'}</td>
                            <td className="py-2.5 px-4">
                                {r.uc_codigo && statusPorUc[r.uc_codigo] && (() => {
                                    const meta = STATUS_RATEIO_UC_META[statusPorUc[r.uc_codigo]];
                                    return (
                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border whitespace-nowrap ${meta.className}`}>
                                            {meta.icon} {meta.label}
                                        </span>
                                    );
                                })()}
                            </td>
                            <td className="py-2.5 px-4 text-gray-700 dark:text-gray-200 max-w-[200px] truncate" title={r.nome_cliente || ''}>{r.nome_cliente || '—'}</td>
                            <td className="py-2.5 px-4 text-gray-500">{r.mes_referencia || '—'}</td>
                            <td className="py-2.5 px-4 text-center text-gray-500">{r.grupo || '—'}</td>
                            <td className="py-2.5 px-4 text-right font-mono text-gray-800 dark:text-gray-100">{r.total_pagar != null ? r.total_pagar.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '—'}</td>
                            <td className="py-2.5 px-4 text-center text-gray-500">{r.score_auditoria ?? '—'}</td>
                            <td className="py-2.5 px-4 text-[12px] whitespace-nowrap">
                                <div className="text-gray-600 dark:text-gray-300 truncate max-w-[160px]" title={r.usuario_nome || ''}>{r.usuario_nome || '—'}</div>
                                <div className="text-gray-400">{fmtData(r.created_at)}</div>
                            </td>
                            <td className="py-2.5 px-4">
                                <div className="flex items-center justify-end gap-1">
                                    {acaoId === r.id ? <Loader2 className="w-4 h-4 animate-spin text-gray-400" /> : (
                                        <>
                                            <button onClick={() => abrirRevisao(r.id, r.pdf_storage_path)} title="Revisar" className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"><Eye className="w-4 h-4" /></button>
                                            <button onClick={() => reprocessar(r.id)} title="Reprocessar (sem novo OCR)" className="p-1.5 rounded-lg text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20"><RefreshCw className="w-4 h-4" /></button>
                                            <button onClick={() => exportarUm(r.id)} title="Exportar Excel" className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"><Download className="w-4 h-4" /></button>
                                        </>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    const cardLote = (id: string, titulo: string, sub: string, qtd: { total: number; ok: number; revisar: number; falhou: number }) => {
        const aberto = abertos.has(id);
        const dados = id === SEM_LOTE ? semLote : faturasDoLote[id];
        return (
            <div key={id} className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
                {/* Cabeçalho clicável + ações lado a lado. Antes as ações eram <button>
                    aninhados DENTRO do <button> do cabeçalho (HTML inválido) e só
                    funcionavam por stopPropagation. */}
                <div className="flex items-center justify-between gap-4 p-4 hover:bg-gray-50/50 dark:hover:bg-gray-800/50">
                    <button onClick={() => toggleLote(id)} className="flex items-center gap-3 min-w-0 flex-1 text-left">
                        {aberto ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />}
                        {id === SEM_LOTE ? <Inbox className="w-4 h-4 text-gray-400 shrink-0" /> : <Package className="w-4 h-4 text-emerald-600 shrink-0" />}
                        <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{titulo}</p>
                            <p className="text-[12px] text-gray-400 truncate">{sub}</p>
                        </div>
                    </button>
                    <div className="flex items-center gap-3 shrink-0">
                        <div className="hidden sm:flex items-center gap-2 text-[11px]">
                            <span className="text-gray-400">{qtd.total} fatura(s)</span>
                            {qtd.ok > 0 && <span className="text-emerald-600 font-medium">{qtd.ok} ok</span>}
                            {qtd.revisar > 0 && <span className="text-amber-600 font-medium">{qtd.revisar} revisar</span>}
                            {qtd.falhou > 0 && <span className="text-red-600 font-medium">{qtd.falhou} falhou</span>}
                        </div>
                        {id !== SEM_LOTE && (
                            <button
                                onClick={() => abrirNaExtracao(id)}
                                disabled={abrindoLoteId === id || qtd.total === 0}
                                title="Abrir este lote na tela de extração (revisar, reprocessar e completar)"
                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] font-medium rounded-lg border border-sky-200 dark:border-sky-800 text-sky-700 dark:text-sky-300 hover:bg-sky-50 dark:hover:bg-sky-900/20 disabled:opacity-50"
                            >
                                {abrindoLoteId === id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FolderOpen className="w-3.5 h-3.5" />} Abrir
                            </button>
                        )}
                        <button
                            onClick={() => abrirPlanilhaLote(id, titulo)}
                            disabled={planilhaLoteId === id || qtd.total === 0}
                            title="Ver planilha na tela (mesmos campos do Excel)"
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] font-medium rounded-lg border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 disabled:opacity-50"
                        >
                            {planilhaLoteId === id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />} Planilha
                        </button>
                        <button
                            onClick={() => {
                                if (zipLote?.id === id) { zipCancelRef.current = true; return; }
                                baixarZipLote(id, titulo);
                            }}
                            disabled={(!!zipLote && zipLote.id !== id) || qtd.total === 0}
                            title={zipLote?.id === id ? 'Cancelar o download do ZIP' : 'Baixar todos os PDFs originais do lote num .zip'}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] font-medium rounded-lg border disabled:opacity-50 ${zipLote?.id === id
                                ? 'border-red-200 dark:border-red-800 text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20'
                                : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                        >
                            {zipLote?.id === id
                                ? <><XCircle className="w-3.5 h-3.5" /> {zipLote.total > 0 ? `${zipLote.feitos}/${zipLote.total} · Cancelar` : 'Cancelar'}</>
                                : <><FileArchive className="w-3.5 h-3.5" /> ZIP</>}
                        </button>
                        <button
                            onClick={() => exportarLote(id)}
                            disabled={exportandoLoteId === id || qtd.total === 0}
                            title="Exportar Excel do lote inteiro"
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                        >
                            {exportandoLoteId === id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Excel
                        </button>
                    </div>
                </div>
                {aberto && (
                    carregandoLote === id ? (
                        <div className="p-8 text-center text-gray-400 border-t border-gray-100 dark:border-gray-800"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
                    ) : dados && dados.length > 0 ? (
                        <div className="border-t border-gray-100 dark:border-gray-800">{renderTabelaFaturas(dados, false)}</div>
                    ) : (
                        <div className="p-6 text-center text-sm text-gray-400 border-t border-gray-100 dark:border-gray-800">Nenhuma fatura neste lote.</div>
                    )
                )}
            </div>
        );
    };

    const totalGeral = useMemo(
        () => contagens ? Object.values(contagens).reduce((s, n) => s + n, 0) : 0,
        [contagens],
    );

    const custos = useMemo(() => {
        if (!contagens) return null;
        const linhas = ORIGENS_EXTRACAO
            .filter(o => contagens[o] > 0)
            .map(o => {
                const faturas = contagens[o];
                const paginas = faturas * PAGINAS_POR_FATURA;
                return { origem: o, faturas, paginas, usd: paginas * USD_POR_PAGINA };
            });
        return {
            linhas,
            totFaturas: linhas.reduce((s, l) => s + l.faturas, 0),
            totPaginas: linhas.reduce((s, l) => s + l.paginas, 0),
            totUsd: linhas.reduce((s, l) => s + l.usd, 0),
        };
    }, [contagens]);

    const abas: { chave: Aba; rotulo: string; contagem: number | null }[] = [
        { chave: 'todas', rotulo: 'Todas', contagem: contagens ? totalGeral : null },
        ...ORIGENS_EXTRACAO.map(o => ({ chave: o as Aba, rotulo: ORIGEM_INFO[o].rotulo, contagem: contagens ? contagens[o] : null })),
    ];

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <Link to="/gestao/extracao" className="p-2 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800"><ArrowLeft className="w-4 h-4" /></Link>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white font-poppins">Histórico de Extrações</h1>
                        <p className="text-gray-500 dark:text-gray-400 mt-0.5 text-sm">Todo uso do robô de extração, separado por onde ele foi acionado.</p>
                    </div>
                </div>
                <button onClick={atualizar} className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"><RefreshCw className="w-4 h-4" /> Atualizar</button>
            </div>

            {/* Abas por origem */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
                {abas.map(({ chave, rotulo, contagem }) => {
                    const ativa = aba === chave;
                    const Icone = chave === 'todas' ? FileSearch : ORIGEM_INFO[chave as OrigemExtracao].icone;
                    const titulo = chave === 'todas' ? 'Todas as origens' : ORIGEM_INFO[chave as OrigemExtracao].descricao;
                    return (
                        <button
                            key={chave}
                            onClick={() => trocarAba(chave)}
                            title={titulo}
                            className={`inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium rounded-xl border whitespace-nowrap transition-all ${ativa
                                ? 'border-primary bg-primary text-white shadow-sm'
                                : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                        >
                            <Icone className="w-3.5 h-3.5" />
                            {rotulo}
                            {contagem !== null && (
                                <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${ativa ? 'bg-white/20' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300'}`}>
                                    {contagem}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Filtros */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 flex flex-col sm:flex-row gap-3">
                <form onSubmit={aplicarBusca} className="flex-1 flex gap-2">
                    <div className="relative flex-1">
                        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="UC, cliente ou CPF/CNPJ" className="w-full pl-9 pr-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-transparent focus:ring-2 focus:ring-emerald-500 outline-none" />
                    </div>
                    <button type="submit" className="px-4 py-2 text-sm font-medium rounded-xl bg-primary text-white hover:bg-primary/80">Buscar</button>
                </form>
                <select value={filtros.status || ''} onChange={e => setFiltros(f => ({ ...f, status: (e.target.value || undefined) as any }))} className="px-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-transparent">
                    <option value="">Todos status</option>
                    <option value="ok">Confere</option>
                    <option value="revisar">Revisar</option>
                    <option value="falhou">Falhou</option>
                </select>
                <select value={filtros.grupo || ''} onChange={e => setFiltros(f => ({ ...f, grupo: (e.target.value || undefined) as any }))} className="px-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-transparent">
                    <option value="">Todos grupos</option>
                    <option value="A">Grupo A</option>
                    <option value="B">Grupo B</option>
                </select>
                {filtrando && (
                    <button type="button" onClick={() => { setBusca(''); setFiltros({}); }} className="px-3 py-2 text-sm font-medium rounded-xl border border-gray-200 dark:border-gray-700 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800">Limpar</button>
                )}
            </div>

            {/* Custos da IA — por origem: cada ponto de entrada gasta o mesmo OCR. */}
            {custos && custos.linhas.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
                    <button onClick={() => setCustosAbertos(a => !a)} className="w-full flex items-center justify-between gap-3 p-4 hover:bg-gray-50/50 dark:hover:bg-gray-800/50 text-left">
                        <div className="flex items-center gap-3">
                            {custosAbertos ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                            <Wallet className="w-4 h-4 text-emerald-600" />
                            <div>
                                <p className="text-sm font-semibold text-gray-900 dark:text-white">Custos da IA (Document AI)</p>
                                <p className="text-[12px] text-gray-400">Quanto cada origem gastou — OCR é pago 1× por upload; reprocessar e exportar custam R$ 0</p>
                            </div>
                        </div>
                        <span className="shrink-0 text-sm font-semibold text-emerald-700 dark:text-emerald-400">{fmtBrl(custos.totUsd * CAMBIO_BRL)} <span className="text-[11px] font-normal text-gray-400">({fmtUsd(custos.totUsd)})</span></span>
                    </button>
                    {custosAbertos && (
                        <div className="border-t border-gray-100 dark:border-gray-800 overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-gray-400 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50">
                                        <th className="py-2.5 px-4 font-medium">Origem</th>
                                        <th className="py-2.5 px-4 font-medium">Onde é acionado</th>
                                        <th className="py-2.5 px-4 font-medium text-right">Faturas</th>
                                        <th className="py-2.5 px-4 font-medium text-right">Páginas OCR*</th>
                                        <th className="py-2.5 px-4 font-medium text-right">Custo (US$)</th>
                                        <th className="py-2.5 px-4 font-medium text-right">Custo (R$)**</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {custos.linhas.map(l => (
                                        <tr key={l.origem} className="border-b border-gray-50 dark:border-gray-800/50">
                                            <td className="py-2 px-4">{origemChip(l.origem)}</td>
                                            <td className="py-2 px-4 text-gray-500 text-[12px]">{ORIGEM_INFO[l.origem].descricao}</td>
                                            <td className="py-2 px-4 text-right font-mono text-gray-700 dark:text-gray-200">{l.faturas}</td>
                                            <td className="py-2 px-4 text-right font-mono text-gray-500">{l.paginas}</td>
                                            <td className="py-2 px-4 text-right font-mono text-gray-700 dark:text-gray-200">{fmtUsd(l.usd)}</td>
                                            <td className="py-2 px-4 text-right font-mono text-gray-700 dark:text-gray-200">{fmtBrl(l.usd * CAMBIO_BRL)}</td>
                                        </tr>
                                    ))}
                                    <tr className="bg-gray-50/70 dark:bg-gray-800/70 font-semibold">
                                        <td className="py-2.5 px-4 text-gray-900 dark:text-white" colSpan={2}>Total</td>
                                        <td className="py-2.5 px-4 text-right font-mono text-gray-900 dark:text-white">{custos.totFaturas}</td>
                                        <td className="py-2.5 px-4 text-right font-mono text-gray-500">{custos.totPaginas}</td>
                                        <td className="py-2.5 px-4 text-right font-mono text-gray-900 dark:text-white">{fmtUsd(custos.totUsd)}</td>
                                        <td className="py-2.5 px-4 text-right font-mono text-emerald-700 dark:text-emerald-400">{fmtBrl(custos.totUsd * CAMBIO_BRL)}</td>
                                    </tr>
                                </tbody>
                            </table>
                            <p className="px-4 py-2.5 text-[11px] text-gray-400">
                                * Estimado em {PAGINAS_POR_FATURA} páginas por fatura (tarifa Document AI: US$ 1,50 / 1.000 páginas). ** Câmbio aproximado de R$ {CAMBIO_BRL.toFixed(2).replace('.', ',')}.
                                Reprocessamentos e exportações reutilizam o OCR salvo no banco — não geram custo novo.
                            </p>
                        </div>
                    )}
                </div>
            )}

            {modoLotes ? (
                <div className="space-y-3">
                    {loadingGrupos ? (
                        <div className="p-16 text-center text-gray-400 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />Carregando…</div>
                    ) : erroGrupos ? (
                        <div className="p-16 text-center text-red-500 text-sm bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700">{erroGrupos}</div>
                    ) : lotes.length === 0 && semLote.length === 0 ? (
                        <div className="p-16 text-center bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700">
                            <FileSearch className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500 font-medium">Nenhuma extração da Gestão</p>
                            <p className="text-sm text-gray-400 mt-1">Processe faturas na tela de extração.</p>
                        </div>
                    ) : (
                        <>
                            {lotes.map(l => cardLote(
                                l.id,
                                `Lote de ${fmtData(l.created_at)}`,
                                l.usuario_nome || 'Usuário não identificado',
                                { total: l.qtd_total, ok: l.qtd_ok, revisar: l.qtd_revisar, falhou: l.qtd_falhou },
                            ))}
                            {semLote.length > 0 && cardLote(
                                SEM_LOTE,
                                'Gestão — sem lote (legado)',
                                'Uploads de antes do agrupamento por lote existir',
                                {
                                    total: semLote.length,
                                    ok: semLote.filter(r => r.status === 'ok').length,
                                    revisar: semLote.filter(r => r.status === 'revisar').length,
                                    falhou: semLote.filter(r => r.status === 'falhou').length,
                                },
                            )}
                        </>
                    )}
                </div>
            ) : (
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
                    {loadingLista && rows.length === 0 ? (
                        <div className="p-16 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />Carregando…</div>
                    ) : erroLista ? (
                        <div className="p-16 text-center text-red-500 text-sm">{erroLista}</div>
                    ) : rows.length === 0 ? (
                        <div className="p-16 text-center">
                            <FileSearch className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500 font-medium">Nenhuma extração encontrada</p>
                            {aba !== 'todas' && !filtrando && (
                                <p className="text-sm text-gray-400 mt-1">{ORIGEM_INFO[aba as OrigemExtracao].descricao} — nada registrado ainda.</p>
                            )}
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-gray-100 dark:border-gray-800">
                                <span className="text-[12px] text-gray-400">
                                    {rows.length} {filtrando ? 'resultado(s)' : 'extração(ões)'}{temMais ? ' carregada(s)' : ''}
                                </span>
                                <button onClick={abrirPlanilhaLista} disabled={preparandoExport} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] font-medium rounded-lg border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 disabled:opacity-50">
                                    {preparandoExport ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />} Ver planilha na tela
                                </button>
                            </div>
                            {renderTabelaFaturas(rows, aba === 'todas' || filtrando)}
                            {temMais && (
                                <div className="p-3 border-t border-gray-100 dark:border-gray-800 text-center">
                                    <button onClick={carregarMais} disabled={loadingLista} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50">
                                        {loadingLista ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronDown className="w-4 h-4" />} Carregar mais
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {revisao && (
                <RevisaoFaturaModal
                    isOpen={!!revisao}
                    onClose={() => setRevisao(null)}
                    arquivoNome={revisao.row.arquivo_nome || undefined}
                    estruturado={revisao.row.dados_estruturados}
                    reconciliacao={revisao.row.reconciliacao}
                    pdfUrl={revisao.pdfUrl}
                    statusRateio={revisao.row.uc_codigo ? statusPorUc[revisao.row.uc_codigo] : undefined}
                />
            )}

            {planilha && (
                <PlanilhaFaturasModal
                    isOpen={!!planilha}
                    onClose={() => setPlanilha(null)}
                    titulo={planilha.titulo}
                    faturas={planilha.faturas}
                    onCorrecoesSalvas={aplicarCorrecoesLocais}
                />
            )}
        </div>
    );
};

export default HistoricoExtracoesView;
